/**
 * /meet/invite/:token  – julkinen kutsulinkin lunastussivu (ei auth).
 *
 * Toiminta:
 *   1) GET /api/meetings/invite/{token}  → label + huoneen otsikko (room_code piilossa)
 *   2) Käyttäjä antaa nimensä (esitäytetty guest_name_prefill jos asetettu)
 *   3) POST /api/meetings/invite/{token}/use  body { name, session_id? }
 *      - session_id tallennetaan localStorageen avaimella `meeting_invite_session_${token}`
 *      - paluuvastaus { room_code, session_id, rejoin } → ohjataan /meet/{room_code}
 *   4) Jos URL on REJOIN (sivu avataan uudelleen samasta selaimesta) → käytetään
 *      tallennettua session_id:tä automaattisesti.
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Video, Lock, RefreshCw } from "lucide-react";
import { Toaster, toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/meetings/invite`;

function loadSessionFor(token) {
  try { return localStorage.getItem(`meeting_invite_session_${token}`); } catch { return null; }
}
function storeSessionFor(token, sid) {
  try { localStorage.setItem(`meeting_invite_session_${token}`, sid); } catch (_e) { /* ignore */ }
}

export default function MeetingInviteRedirect() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [name, setName] = useState("");
  const [error, setError] = useState(null);
  const [joining, setJoining] = useState(false);
  const [hasSession, setHasSession] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const storedSid = loadSessionFor(token);
        const url = storedSid ? `${API}/${token}?sid=${encodeURIComponent(storedSid)}` : `${API}/${token}`;
        const r = await axios.get(url);
        if (cancelled) return;
        setInfo(r.data);
        setName(r.data.guest_name_prefill || "");
        setHasSession(!!storedSid);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Kutsulinkki on virheellinen.");
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [token]);

  const doJoin = async (preferStoredSession = true) => {
    if (!name.trim()) {
      toast.error("Anna nimesi");
      return;
    }
    setJoining(true);
    try {
      const stored = preferStoredSession ? loadSessionFor(token) : null;
      const r = await axios.post(`${API}/${token}/use`, {
        name: name.trim(),
        session_id: stored || undefined,
      });
      const { room_code, session_id, rejoin } = r.data;
      storeSessionFor(token, session_id);
      if (rejoin) toast.success("Yhdistetään uudelleen kokoukseen…");
      navigate(`/meet/${room_code}?name=${encodeURIComponent(name.trim())}&inv=${token}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Liittyminen epäonnistui";
      toast.error(detail);
      setError(detail);
    }
    setJoining(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-white/40" />
      </div>
    );
  }

  if (error || !info) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-black to-zinc-950 text-white flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-4" data-testid="invite-error">
          <div className="w-16 h-16 mx-auto rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center">
            <Lock className="w-8 h-8 text-red-300" />
          </div>
          <h1 className="text-2xl font-display">Kutsulinkki ei toimi</h1>
          <p className="text-white/60 text-sm">{error || "Pyydä järjestäjältä uusi linkki."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-black to-zinc-950 text-white flex items-center justify-center p-6" data-testid="invite-redirect-page">
      <Toaster richColors position="top-center" />
      <div className="max-w-md w-full space-y-5">
        <div className="text-center space-y-1">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-cyan-400 to-sky-600 flex items-center justify-center shadow-2xl shadow-cyan-500/30 mb-3">
            <Video className="w-7 h-7 text-white" />
          </div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-cyan-300/80">Kutsu kokoukseen</p>
          <h1 className="text-2xl font-display tracking-tight">{info.title || "Kokoushuone"}</h1>
          {info.label && (
            <p className="text-sm text-amber-300 mt-1">Kutsu: {info.label}</p>
          )}
          <p className="text-xs text-white/40">Järjestäjä: {info.owner_name}</p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-4">
          <div>
            <Label className="text-xs text-white/60">Nimesi</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Etu- ja sukunimi"
              data-testid="invite-name"
              autoFocus
            />
          </div>

          <Button
            onClick={() => doJoin(true)}
            disabled={joining || !name.trim()}
            className="btn-luxe w-full h-11 inline-flex items-center justify-center gap-2"
            data-testid="invite-join"
          >
            {joining ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Liitytään…
              </>
            ) : (
              <>
                <Video className="w-4 h-4" />
                {(hasSession && info.allow_rejoin) || info.is_rejoin ? "Liity uudelleen kokoukseen" : "Liity kokoukseen"}
              </>
            )}
          </Button>

          {((hasSession && info.allow_rejoin) || info.is_rejoin) && (
            <p className="text-[10px] text-emerald-300/80 text-center inline-flex items-center justify-center gap-1" data-testid="rejoin-hint">
              <RefreshCw className="w-3 h-3" />
              Pudotuksen jälkeen palaat samaan istuntoon
            </p>
          )}

          {info.max_uses > 0 && (
            <p className="text-[10px] text-white/40 text-center">
              Käyttömäärä: {info.used_count} / {info.max_uses}
            </p>
          )}
        </div>

        <p className="text-center text-[10px] text-white/30">
          Apps2Go Hub · Turvallinen kertakäyttöinen kutsulinkki
        </p>
      </div>
    </div>
  );
}
